/**
 * HelpSearch component - search Git-authored help articles.
 * Used in Help menu or dedicated Help page.
 */
import React, { useState } from 'react';
import {
    Stack,
    TextField,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { CompactListSurface } from './CompactListSurface';
import { searchSiteHelp, SiteHelpEntry } from '../config/siteHelpContent';
import { getSiteHref } from '../config/siteLinks';
import { useDataEnvironment } from '../contexts/DataEnvironmentContext';
import { ButtonLoadingLabel } from './common/LoadingStates';

interface HelpSearchResult {
    slug: string;
    title: string;
    excerpt: string;
    href: string;
}

export const HelpSearch: React.FC = () => {
    const { dataEnvironment } = useDataEnvironment();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<HelpSearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searched, setSearched] = useState(false);

    const handleSearch = async () => {
        if (!query.trim()) return;

        setLoading(true);
        setError(null);
        setSearched(true);
        try {
            const siteResults = searchSiteHelp(query).map((entry: SiteHelpEntry) => ({
                slug: entry.slug,
                title: entry.title,
                excerpt: entry.summary,
                href: getSiteHref(entry.guideHref || `/help/${entry.slug}`, dataEnvironment),
            }));
            setResults(siteResults);
        } catch (e: any) {
            setError(e.message || 'Failed to search help');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleResultClick = (href: string) => {
        window.open(href, '_blank');
    };

    return (
        <Stack spacing="var(--utility-stack-gap)">
            <Stack direction="row" spacing="var(--utility-inline-gap)">
                <TextField
                    fullWidth
                    label="Search help articles"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    variant="outlined"
                    size="small"
                />
                <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loading || !query.trim()}
                    startIcon={loading ? undefined : <SearchIcon />}
                >
                    {loading ? <ButtonLoadingLabel label="Searching" /> : "Search"}
                </Button>
            </Stack>

            {error && (
                <Typography variant="body2" color="text.secondary">
                    Help content not found for topic: search
                </Typography>
            )}

            {searched && !loading && results.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No help articles found for "{query}"
                </Typography>
            )}

            {results.length > 0 && (
                <CompactListSurface sx={{ minHeight: 0, maxHeight: 320, mt: "var(--result-surface-margin-top)" }}>
                    <List dense sx={{ p: "var(--result-surface-padding)" }}>
                        {results.map((result) => (
                            <ListItem key={result.slug} disablePadding>
                                <ListItemButton
                                    onClick={() => handleResultClick(result.href)}
                                    sx={{
                                        px: "var(--result-row-padding-inline)",
                                        py: "var(--result-row-padding-block)",
                                        borderRadius: "var(--result-surface-radius)",
                                    }}
                                >
                                    <ListItemText
                                        primary={result.title}
                                        primaryTypographyProps={{ variant: "body2", color: "var(--metadata-value-color)" }}
                                        secondaryTypographyProps={{
                                            component: 'div',
                                            variant: "body2",
                                            color: "var(--metadata-label-color)",
                                        }}
                                        secondary={
                                            <div
                                                dangerouslySetInnerHTML={{ __html: result.excerpt }}
                                            />
                                        }
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </CompactListSurface>
            )}
        </Stack>
    );
};
